import {
  useMutation, UseMutationOptions, UseMutationResult, useQuery, UseQueryOptions, UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../../axios';
import { responseHandler } from '../../../utils';
import {
  ClaimKdsDeviceRequest, ClaimKdsDeviceResult, KdsDevice, KdsEnvelope, UnpairKdsDeviceRequest,
} from './kdsTypes';

export const kdsDevicesQueryKey = (hotelId: string | null | undefined) => ['kds-devices', hotelId] as const;

/**
 * Lista ekranów KDS hotelu. Nie czytamy `kdsDevices` z Firestore, bo kolekcja jest
 * backend-only (trzyma hashe sekretów urządzeń) — dane idą przez endpoint v3.
 */
export const useGetKdsDevices = (
  hotelId: string | null | undefined,
  options?: Omit<UseQueryOptions<KdsDevice[], AxiosError>, 'queryKey' | 'queryFn'>,
): UseQueryResult<KdsDevice[], AxiosError> => useQuery<KdsDevice[], AxiosError>({
  queryKey: kdsDevicesQueryKey(hotelId),
  queryFn: async () => {
    if (!hotelId) return [];
    const body = await axiosInstance
      .post<KdsEnvelope<{ devices: KdsDevice[] }>>('/v3-listKdsDevices', { hotelId })
      .then(responseHandler);
    return body.data?.devices ?? [];
  },
  enabled: !!hotelId,
  retry: 0,
  ...options,
});

type ClaimMutation = UseMutationResult<ClaimKdsDeviceResult, AxiosError, ClaimKdsDeviceRequest>;

/** Przypisanie tabletu do hotelu kodem z jego ekranu. */
export const useClaimKdsDevice = (
  options?: UseMutationOptions<ClaimKdsDeviceResult, AxiosError, ClaimKdsDeviceRequest>,
): ClaimMutation => useMutation<ClaimKdsDeviceResult, AxiosError, ClaimKdsDeviceRequest>({
  mutationFn: async (payload) => {
    const body = await axiosInstance
      .post<KdsEnvelope<ClaimKdsDeviceResult>>('/v3-claimKdsDevice', payload)
      .then(responseHandler);
    return body.data;
  },
  ...options,
});

/** Odłączenie ekranu — kasuje jego konto, więc tablet natychmiast traci dostęp. */
export const useUnpairKdsDevice = (
  options?: UseMutationOptions<void, AxiosError, UnpairKdsDeviceRequest>,
): UseMutationResult<void, AxiosError, UnpairKdsDeviceRequest> => useMutation<void, AxiosError, UnpairKdsDeviceRequest>({
  mutationFn: async (payload) => {
    await axiosInstance.post('/v3-unpairKdsDevice', payload).then(responseHandler);
  },
  ...options,
});
